import { useState, useCallback } from "react";
import { useGameStore } from "../store/useGameStore.js";
import { useFlyAnimation } from "../contexts/FlyAnimationContext.js";
import type { Card, GameState } from "@solitaire/game-engine";

// ============================================================
// DebugCardPanel — Karten-Status-Debugger
// Eingabe: z.B. "pik 6", "herz dame", "kreuz as"
// Zeigt: Position im GameState, DOM-Rect, isFlying, CSS-Visibility
// ============================================================

const SUIT_MAP: Record<string, string> = {
  pik: "spades", spades: "spades", p: "spades", "♠": "spades",
  herz: "hearts", hearts: "hearts", h: "hearts", "♥": "hearts",
  karo: "diamonds", diamonds: "diamonds", k: "diamonds", "♦": "diamonds",
  kreuz: "clubs", clubs: "clubs", kr: "clubs", "♣": "clubs",
};

const RANK_MAP: Record<string, number> = {
  as: 1, a: 1, ace: 1, "1": 1,
  "2": 2, "3": 3, "4": 4, "5": 5, "6": 6, "7": 7, "8": 8, "9": 9, "10": 10,
  bube: 11, b: 11, jack: 11, j: 11,
  dame: 12, d: 12, queen: 12, q: 12,
  könig: 13, kg: 13, king: 13, "13": 13,
};

function parseCardInput(input: string): string | null {
  const parts = input.trim().toLowerCase().split(/[\s-]+/);
  if (parts.length < 2) return null;

  const suit = SUIT_MAP[parts[0]!];
  const rank = RANK_MAP[parts[1]!];
  if (!suit || !rank) return null;

  return `${suit}-${rank}`;
}

function findCardLocation(state: GameState, cardId: string): string {
  // Stock
  for (let i = 0; i < state.stock.length; i++) {
    if (state.stock[i]!.id === cardId) return `Stock (Position ${i + 1}/${state.stock.length})`;
  }
  // Waste
  for (let i = 0; i < state.waste.length; i++) {
    if (state.waste[i]!.id === cardId) {
      const isTop = i === state.waste.length - 1;
      return `Waste (Position ${i + 1}/${state.waste.length}${isTop ? ", TOP" : ""})`;
    }
  }
  // Foundation
  for (let fi = 0; fi < 4; fi++) {
    const pile = state.foundation[fi]!;
    for (let i = 0; i < pile.length; i++) {
      if (pile[i]!.id === cardId) {
        const isTop = i === pile.length - 1;
        return `Foundation ${fi} (Position ${i + 1}/${pile.length}${isTop ? ", TOP" : ""})`;
      }
    }
  }
  // Tableau
  for (let col = 0; col < 7; col++) {
    const column = state.tableau[col]!;
    for (let i = 0; i < column.length; i++) {
      if (column[i]!.id === cardId) {
        const card = column[i]!;
        const isTop = i === column.length - 1;
        return `Tableau ${col} (Position ${i + 1}/${column.length}${isTop ? ", TOP" : ""}, ${card.faceUp ? "face-up" : "face-DOWN"})`;
      }
    }
  }
  return "NICHT GEFUNDEN!";
}

function findCard(state: GameState, cardId: string): Card | null {
  for (const pile of [state.stock, state.waste]) {
    const c = pile.find((c) => c.id === cardId);
    if (c) return c;
  }
  for (const pile of state.foundation) {
    const c = pile.find((c) => c.id === cardId);
    if (c) return c;
  }
  for (const col of state.tableau) {
    const c = col.find((c) => c.id === cardId);
    if (c) return c;
  }
  return null;
}

interface CardDebugInfo {
  cardId: string;
  card: Card | null;
  location: string;
  domExists: boolean;
  rect: { x: number; y: number; w: number; h: number } | null;
  visibility: string;
  opacity: string;
  flying: boolean;
  autoCompleting: boolean;
  flyingIdsCount: number;
}

export function DebugCardPanel() {
  const [input, setInput] = useState("");
  const [info, setInfo] = useState<CardDebugInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [allCards, setAllCards] = useState<{ id: string; location: string }[] | null>(null);
  const { isFlying } = useFlyAnimation();

  const lookup = useCallback(() => {
    setAllCards(null);
    const cardId = parseCardInput(input);
    if (!cardId) {
      setError(`Kann "${input}" nicht parsen. Beispiele: "pik 6", "herz dame", "kreuz as"`);
      setInfo(null);
      return;
    }
    setError(null);

    const { gameState, isAutoCompleting } = useGameStore.getState();
    const card = findCard(gameState, cardId);
    const location = findCardLocation(gameState, cardId);

    // DOM lookup
    const el = document.querySelector(`[data-card-id="${cardId}"]`);
    let rect: CardDebugInfo["rect"] = null;
    let visibility = "–";
    let opacity = "–";

    if (el) {
      const r = el.getBoundingClientRect();
      rect = { x: Math.round(r.left), y: Math.round(r.top), w: Math.round(r.width), h: Math.round(r.height) };
      const styles = getComputedStyle(el);
      visibility = styles.visibility;
      opacity = styles.opacity;
    }

    // flyingIds Zähler
    const flyingIdsCount = document.querySelectorAll('[style*="visibility: hidden"]').length;

    setInfo({
      cardId,
      card,
      location,
      domExists: !!el,
      rect,
      visibility,
      opacity,
      flying: isFlying(cardId),
      autoCompleting: isAutoCompleting,
      flyingIdsCount,
    });
  }, [input, isFlying]);

  const showAll = useCallback(() => {
    setInfo(null);
    setError(null);
    const { gameState } = useGameStore.getState();
    const cards: { id: string; location: string }[] = [];

    // Alle 52 Karten durchgehen
    const suits = ["clubs", "diamonds", "hearts", "spades"];
    for (const suit of suits) {
      for (let rank = 1; rank <= 13; rank++) {
        const id = `${suit}-${rank}`;
        const location = findCardLocation(gameState, id);
        const el = document.querySelector(`[data-card-id="${id}"]`);
        const fly = isFlying(id);
        const vis = el ? getComputedStyle(el).visibility : "–";
        const opa = el ? getComputedStyle(el).opacity : "–";
        const status = !el
          ? "NO-DOM"
          : fly
            ? "FLYING"
            : vis === "hidden"
              ? "HIDDEN!"
              : opa === "0"
                ? "OPACITY-0"
                : "ok";
        if (status !== "ok") {
          cards.push({ id, location: `${location} | DOM=${el ? "yes" : "NO"} | flying=${fly} | vis=${vis} | opa=${opa} | STATUS: ${status}` });
        }
      }
    }
    setAllCards(cards.length > 0 ? cards : [{ id: "–", location: "Alle 52 Karten sichtbar und im DOM" }]);
  }, [isFlying]);

  return (
    <div style={{
      position: "fixed",
      bottom: 0,
      left: 0,
      right: 0,
      background: "rgba(0,0,0,0.9)",
      color: "#0f0",
      fontFamily: "monospace",
      fontSize: "11px",
      padding: "6px 10px",
      zIndex: 10000,
      maxHeight: "40vh",
      overflowY: "auto",
    }}>
      <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 4 }}>
        <span style={{ color: "#ff0", fontWeight: "bold" }}>DEBUG</span>
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && lookup()}
          placeholder="z.B. pik 6, herz dame"
          style={{
            background: "#222",
            color: "#0f0",
            border: "1px solid #444",
            padding: "2px 6px",
            fontFamily: "monospace",
            fontSize: "11px",
            width: 180,
          }}
        />
        <button onClick={lookup} style={{ background: "#333", color: "#0f0", border: "1px solid #555", padding: "2px 8px", cursor: "pointer", fontSize: "11px" }}>
          Suchen
        </button>
        <button onClick={showAll} style={{ background: "#333", color: "#ff0", border: "1px solid #555", padding: "2px 8px", cursor: "pointer", fontSize: "11px" }}>
          Probleme?
        </button>
        <span style={{ color: "#888", marginLeft: "auto" }}>
          AutoComplete: {useGameStore((s) => s.isAutoCompleting) ? "AKTIV" : "–"}
        </span>
      </div>

      {error && <div style={{ color: "#f55" }}>{error}</div>}

      {info && (
        <div style={{ lineHeight: 1.5 }}>
          <div>
            <span style={{ color: "#ff0" }}>Karte:</span> {info.cardId}
            {info.card && ` (${info.card.suit} ${info.card.rank}, ${info.card.faceUp ? "face-up" : "face-DOWN"})`}
          </div>
          <div>
            <span style={{ color: "#ff0" }}>State:</span> {info.location}
          </div>
          <div>
            <span style={{ color: "#ff0" }}>DOM:</span>{" "}
            {info.domExists ? (
              <>
                JA | x={info.rect?.x} y={info.rect?.y} w={info.rect?.w} h={info.rect?.h}
              </>
            ) : (
              <span style={{ color: "#f55" }}>NICHT IM DOM!</span>
            )}
          </div>
          <div>
            <span style={{ color: "#ff0" }}>CSS:</span> visibility={info.visibility} | opacity={info.opacity}
          </div>
          <div>
            <span style={{ color: "#ff0" }}>Flying:</span>{" "}
            <span style={{ color: info.flying ? "#f55" : "#0f0" }}>{info.flying ? "JA ⚠" : "nein"}</span>
          </div>
          <div style={{ color: "#888" }}>
            Hidden-Elements im DOM: {info.flyingIdsCount}
          </div>
        </div>
      )}

      {allCards && (
        <div style={{ lineHeight: 1.4 }}>
          <div style={{ color: "#ff0", marginBottom: 2 }}>Karten mit Problemen:</div>
          {allCards.map((c) => (
            <div key={c.id}>
              <span style={{ color: "#f88" }}>{c.id}</span>: {c.location}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
